import { useState, useEffect } from "react";
import { PlusCircle, MinusCircle, Pencil, Check } from "lucide-react";
import { Button } from "rsuite";
import Input from "@components/forms/Input";
import { useTranslation } from "react-i18next";
import { addInfoItem, saveInfoItem } from "../Actions";

/**
 * Reusable Dynamic Input List Component with per-input edit/save integration
 *
 * @param {number|string} productId - Product ID for backend route
 * @param {string} model - Model name (e.g. "included", "excluded")
 * @param {Array} initialValues - Initial list of items [{id, value}]
 * @param {number} maxItems - Max number of inputs allowed
 * @param {string} placeholder - Placeholder text
 * @param {function} onChange - Callback when list changes
 * @param {boolean} showLabels - Whether to show add/remove labels
 */
const DynamicInputList = ({
  productId,
  model,
  initialValues = [],
  maxItems = 5,
  placeholder = "",
  onChange = () => {},
  showLabels = true,
}) => {
  const { t } = useTranslation();
  const [forms, setForms] = useState([]);

  // Initialize list (each item has editable state)
  useEffect(() => {
    const prepared = initialValues.map((item, i) => ({
      id: item.id || i + 1,
      value: item.value || item,
      editing: false,
      type: "update",
    }));
    setForms(
      prepared.length
        ? prepared
        : [{ id: Date.now(), value: "", editing: true }]
    );
  }, [initialValues]);

  // Sync to parent
  useEffect(() => {
    onChange(forms);
  }, [forms]);

  // Input change handler
  const handleInputChange = (id, value) => {
    setForms((prev) => prev.map((f) => (f.id === id ? { ...f, value } : f)));
  };

  // Toggle edit mode
  const toggleEdit = (id) => {
    setForms((prev) =>
      prev.map((f) => (f.id === id ? { ...f, editing: !f.editing } : f))
    );
  };

  // Save to backend
  const handleSave = async (id) => {
    const item = forms.find((f) => f.id === id);
    if (!item?.value?.trim()) return;

    try {
      let res;
      // If backend-generated id exists → update
      if (item.type === "update") {
        res = await saveInfoItem(productId, model, id, item.value);
      } else {
        // new item (Date.now() used)
        res = await addInfoItem(productId, model, item.value);
      }

      toggleEdit(id);
    } catch (err) {
      console.error(`❌ Failed to save ${model} item:`, err);
    }
  };

  // Add new input
  const addForm = () => {
    if (forms.length < maxItems) {
      setForms((prev) => [
        ...prev,
        { id: Date.now(), value: "", editing: true, type: "new" },
      ]);
    }
  };

  // Remove input
  const removeForm = (id) => {
    setForms((prev) => prev.filter((f) => f.id !== id));
  };

  return (
    <div className="mt-1">
      {forms.map((form) => (
        <div
          key={form.id}
          className="my-3 p-3 d-flex flex-column"
          style={{
            borderColor: form.editing ? "#6610f2" : "#ddd",
            backgroundColor: form.editing ? "#f9f6ff" : "#fff",
            transition: "0.2s ease",
          }}
        >
          <div className="d-flex align-items-center gap-2">
            <Input
              type="text"
              value={form.value}
              placeholder={placeholder}
              handleChange={(e) => handleInputChange(form.id, e.target.value)}
              disabled={!form.editing}
              className="flex-grow-1"
            />
            <Button
              appearance={form.editing ? "primary" : "ghost"}
              className="p-3 flex items-center justify-center"
              style={{
                borderRadius: 200,
                backgroundColor: form.editing ? "#6610f2" : "transparent",
                color: form.editing ? "#fff" : "#6610f2",
                borderColor: "#6610f2",
                minWidth: "40px", // keeps it square
              }}
              onClick={() =>
                form.editing ? handleSave(form.id) : toggleEdit(form.id)
              }
            >
              {form.editing ? <Check size={20} /> : <Pencil size={20} />}
            </Button>
          </div>
        </div>
      ))}

      {forms.length < maxItems && (
        <button
          className="bg-transparent text-primary border-0 mt-2"
          type="button"
          onClick={addForm}
          style={{ color: "#6610f2" }}
        >
          <PlusCircle strokeWidth={1.5} /> {showLabels && t("add")}
        </button>
      )}
    </div>
  );
};

export default DynamicInputList;
